/**
 * DevicesPanel — "Devices & sessions", the settings half of the device cap.
 * Shared verbatim by the desktop route (`/account/devices`) and the mobile
 * screen (`/devices`); only the chrome around it differs.
 *
 * This is the half of the surface that did not exist at all: `listDevices()`
 * had ZERO callers, there was no route in either shell, and the only way a user
 * could ever see their sessions was to hit the cap. So the product's answer to
 * "which devices am I signed in on?" was "sign in on a fourth one and find
 * out", and the answer to "I lost my laptop" was nothing.
 *
 * ── DESIGN NOTES THAT ARE ACTUALLY DECISIONS ──────────────────────────────
 * · Same `<SessionRail>` as the blocking interstitial, in `mode="manage"`. One
 *   component, so the two screens cannot drift into two vocabularies for the
 *   same three facts.
 * · Unused slots are DASHED ROWS, not a caption. "2 of 3" is a number you read;
 *   an empty slot on the rail is a thing you can count, and it makes the cap
 *   feel like capacity rather than a punishment.
 * · NO CONFIRM DIALOG on signing a session out. It is reversible — you sign
 *   back in — and the standing line under the meter says so permanently. A
 *   modal here would be friction on the one action this screen exists for.
 *   `feedback.dim()` (a single falling note) carries the weight instead;
 *   `success()`'s two-note rise would be a lie about what just happened.
 * · A 404 `unknown_device` REMOVES THE ROW anyway. That session had already
 *   ended; a stale row you can press forever and never remove is its own
 *   small dead end.
 * · The whole screen renders an explanation, never an error, when
 *   `DEVICE_LIMIT_LIVE` is off — which is today's prod.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useReducedMotion } from 'framer-motion'
import { useDevices } from '../lib/useDevices'
import { feedback } from '../lib/feedback'
import { PressScale } from '../lib/PressScale'
import { SessionRail } from './devices/SessionRail'

type Note = { tone: 'quiet' | 'danger'; text: string }

export function DevicesPanel({ onBack }: { onBack: () => void }) {
  const still = useReducedMotion() === true
  const { status, devices, limit, exempt, used, freeSlots, error, reload, revoke } = useDevices()
  const [busyId, setBusyId] = useState<string | null>(null)
  const [notes, setNotes] = useState<Record<string, Note | undefined>>({})

  /**
   * ── WHAT HAPPENS TO FOCUS, AND WHAT A SCREEN READER HEARS ─────────────────
   * "Sign out" lives inside the row it deletes. When the row goes, focus goes
   * to <body> — top of the document, on a settings screen the user reached by
   * tabbing — and the only acknowledgement was `feedback.dim()`, which is a
   * sound and a haptic: nothing at all for a screen-reader user, who was left
   * on a list that had silently become one shorter. So the outcome is spoken
   * once, and focus lands on the rail the row was in.
   */
  const railRef = useRef<HTMLDivElement>(null)
  const [announcement, setAnnouncement] = useState('')
  const [refocus, setRefocus] = useState(0)
  useEffect(() => {
    if (refocus === 0) return
    railRef.current?.focus()
  }, [refocus])

  const onRevoke = useCallback(async (deviceId: string) => {
    const label = devices.find((d) => d.deviceId === deviceId)?.label ?? 'That session'
    setBusyId(deviceId)
    setNotes((n) => ({ ...n, [deviceId]: undefined }))
    const r = await revoke(deviceId)
    setBusyId(null)
    if (r.kind === 'ok' || r.kind === 'gone') {
      // `gone` too: that session had already ended, the row leaves either way,
      // and "it was already signed out" is not a failure to report.
      feedback.dim()
      setAnnouncement(`${label} signed out. ${r.kind === 'gone' ? 'It had already ended.' : ''}`.trim())
      setRefocus((n) => n + 1)
      return
    }
    feedback.deny()
    setNotes((n) => ({
      ...n,
      [deviceId]: {
        tone: 'danger',
        text: r.kind === 'off'
          ? 'Session management isn’t switched on for your account.'
          : 'That didn’t go through.',
      },
    }))
  }, [devices, revoke])

  if (status === 'unavailable') {
    return (
      <Frame>
        <h1 className="font-serif text-2xl tracking-h2 text-navy-900">Devices &amp; sessions</h1>
        <p className="mt-3 text-sm leading-relaxed text-navy-500">
          Session management isn’t switched on for your account yet. When it is, you’ll be able to
          see every device you’re signed in on and sign any of them out from here.
        </p>
        <PressScale feel="tap" className="btn-ghost min-h-11 mt-6" onClick={onBack} data-testid="devices-back">
          Back
        </PressScale>
      </Frame>
    )
  }

  if (status === 'error') {
    return (
      <Frame>
        <h1 className="font-serif text-2xl tracking-h2 text-navy-900">Devices &amp; sessions</h1>
        <p className="mt-3 text-sm leading-relaxed text-navy-500" role="status">
          Couldn’t load your sessions. {error}
        </p>
        <div className="mt-6 flex flex-wrap gap-2.5">
          <PressScale feel="tap" className="btn-primary" onClick={reload} data-testid="devices-retry">
            Try again
          </PressScale>
          <PressScale feel="tap" className="btn-ghost min-h-11" onClick={onBack} data-testid="devices-back">
            Back
          </PressScale>
        </div>
      </Frame>
    )
  }

  return (
    <Frame>
      <h1 className="font-serif text-2xl tracking-h2 text-navy-900">Devices &amp; sessions</h1>

      {status === 'loading' || status === 'idle' ? (
        <div className="mt-5" aria-busy="true" aria-label="Loading your sessions">
          <div className="skeleton h-4 w-32 rounded-sm" />
          {/* h-[56px] + 12px gaps == the real row's min-height and MIN_GAP,
              so the list does not jump when the data lands. */}
          <div className="mt-4 space-y-3">
            <div className="skeleton h-[56px] rounded-md" />
            <div className="skeleton h-[56px] rounded-md" />
            <div className="skeleton h-[56px] rounded-md" />
          </div>
        </div>
      ) : (
        <>
          <p className="mt-2 text-sm text-navy-500 tabular" data-testid="devices-meter">
            {exempt ? 'No session limit on this account.' : `${used} of ${limit} signed in`}
          </p>

          {/* One announcement per revoke, after the list has settled. */}
          <span className="sr-only" aria-live="polite" data-testid="devices-live">{announcement}</span>

          <div ref={railRef} tabIndex={-1} role="group" className="mt-5 focus:outline-none" aria-label="Your sessions">
            <SessionRail
              devices={devices}
              mode="manage"
              still={still}
              freeSlots={exempt ? 0 : freeSlots}
              busyId={busyId}
              notes={notes}
              onRevoke={(id) => { void onRevoke(id) }}
            />
          </div>

          {/* The undo, stated permanently instead of hidden in a confirm modal. */}
          <p className="mt-5 border-t border-line pt-4 text-xs leading-relaxed text-navy-500">
            Signing a session out doesn’t change anything on that device except the sign-in — you
            can sign back in on it any time. Diaex allows {exempt ? 'unlimited' : limit} sessions per
            person, not per company.
          </p>

          <PressScale feel="tap" className="btn-ghost min-h-11 mt-4" onClick={onBack} data-testid="devices-back">
            Back
          </PressScale>
        </>
      )}
    </Frame>
  )
}

function Frame({ children }: { children: React.ReactNode }) {
  return <div className="mx-auto w-full max-w-[34rem] px-5 py-8 sm:px-6">{children}</div>
}

export default DevicesPanel
